import { useState } from 'react';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { Badge, Champ, Infobulle } from './Elements';
import type { ModeleDocument, TypeDocument } from '../types';

/**
 * LES TYPES DE DOCUMENTS d'une école (ADR-0014, 2026-10-03) : ce que
 * l'application archive, et où elle le range sous la racine du Drive.
 * Même grammaire que les variables de paie : le bloc écrit immédiatement,
 * chaque geste est un acte à part.
 *
 * Le MODÈLE est une liste fermée — les rendus que le code connaît — plus
 * « Aucun » pour un type dont les fichiers sont déposés à la main (les
 * fiches de paie). Un champ libre désignerait du code que personne ne
 * teste. La liste est le pendant de `MODELES` côté serveur : les deux
 * doivent rester d'accord.
 */
const MODELES: { valeur: ModeleDocument | ''; libelle: string }[] = [
  { valeur: '', libelle: 'Aucun — déposé à la main' },
  { valeur: 'bordereau_remise', libelle: 'Bordereau de remise' },
];

const libelleModele = (modele: ModeleDocument | null) =>
  MODELES.find((m) => m.valeur === (modele ?? ''))?.libelle ?? modele ?? '';

export function TypesDocuments({ entiteId, peutModifier }: { entiteId: string; peutModifier: boolean }) {
  const types = useRessource(
    () => api.get<TypeDocument[]>(`/api/admin/entites/${entiteId}/types-documents`),
    [entiteId],
  );
  const vide = { nature: '', repertoire: '', modele: '' as ModeleDocument | '' };
  const [saisie, setSaisie] = useState(vide);
  const [edition, setEdition] = useState<{ id: string; nature: string; repertoire: string; modele: ModeleDocument | '' } | null>(
    null,
  );
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const agir = async (travail: () => Promise<unknown>) => {
    setErreur(null);
    setEnCours(true);
    try {
      await travail();
      types.recharger();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? (cause.details?.[0]?.message ?? cause.message) : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const ajouter = () => {
    if (!saisie.nature.trim() || !saisie.repertoire.trim()) {
      setErreur('Indiquez une nature et un répertoire.');
      return;
    }
    void agir(async () => {
      await api.post(`/api/admin/entites/${entiteId}/types-documents`, {
        nature: saisie.nature.trim(),
        repertoire: saisie.repertoire.trim(),
        modele: saisie.modele || null,
      });
      setSaisie(vide);
    });
  };

  const enregistrerEdition = () => {
    if (!edition) return;
    void agir(async () => {
      await api.patch(`/api/admin/entites/${entiteId}/types-documents/${edition.id}`, {
        nature: edition.nature.trim(),
        repertoire: edition.repertoire.trim(),
        modele: edition.modele || null,
      });
      setEdition(null);
    });
  };

  const basculer = (t: TypeDocument) =>
    void agir(() => api.patch(`/api/admin/entites/${entiteId}/types-documents/${t.id}`, { actif: !t.actif }));

  // Un seul type porte les fiches de paie : le serveur retire le marqueur de l'autre.
  const basculerFichePaie = (t: TypeDocument) =>
    void agir(() =>
      api.patch(`/api/admin/entites/${entiteId}/types-documents/${t.id}`, { fichePaie: !t.fiche_paie }),
    );

  const supprimer = (t: TypeDocument) => {
    if (!window.confirm(`Supprimer le type « ${t.nature} » ?`)) return;
    void agir(() => api.delete(`/api/admin/entites/${entiteId}/types-documents/${t.id}`));
  };

  const selectModele = (valeur: ModeleDocument | '', onChange: (v: ModeleDocument | '') => void) => (
    <select className="champ py-1.5" value={valeur} onChange={(e) => onChange(e.target.value as ModeleDocument | '')}>
      {MODELES.map((m) => (
        <option key={m.valeur} value={m.valeur}>
          {m.libelle}
        </option>
      ))}
    </select>
  );

  return (
    <div className="space-y-3">
      <div className="etiquette">Types de documents</div>

      {peutModifier && (
        <div className="grid grid-cols-[10rem_1fr_14rem_auto] items-end gap-2">
          <Champ libelle="Nature">
            <input
              className="champ py-1.5"
              placeholder="Fiche de paie"
              value={saisie.nature}
              onChange={(e) => setSaisie({ ...saisie, nature: e.target.value })}
            />
          </Champ>
          <Champ libelle="Répertoire sous la racine">
            <input
              className="champ tabulaire py-1.5"
              placeholder="RH/Fiches de paie/{annee}/{salarie}"
              value={saisie.repertoire}
              onChange={(e) => setSaisie({ ...saisie, repertoire: e.target.value })}
            />
          </Champ>
          <Champ libelle="Modèle">{selectModele(saisie.modele, (modele) => setSaisie({ ...saisie, modele }))}</Champ>
          <div className="block">
            <span className="etiquette invisible">Ajouter</span>
            <button type="button" className="bouton-secondaire block py-1.5" disabled={enCours} onClick={ajouter}>
              Ajouter
            </button>
          </div>
        </div>
      )}

      {types.erreur && <p className="text-sm text-red-700">{types.erreur}</p>}

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-encre-500">
            <th className="py-1 pr-2 font-medium">Nature</th>
            <th className="py-1 pr-2 font-medium">Répertoire sous la racine</th>
            <th className="py-1 pr-2 font-medium">Modèle</th>
            <th className="py-1 pr-2 text-center font-medium">
              <Infobulle texte="C’est dans ce type que l’envoi aux salariés cherche le bulletin du mois. Un seul par école, et son répertoire doit contenir {salarie}.">
                Fiches de paie
              </Infobulle>
            </th>
            <th className="py-1 pr-2 font-medium">État</th>
            <th />
          </tr>
        </thead>
        <tbody className="divide-y divide-encre-100">
          {(types.donnees ?? []).length === 0 && !types.chargement && (
            <tr>
              <td colSpan={6} className="py-2 text-encre-400">
                Aucun type : rien ne sera archivé sur le Drive.
              </td>
            </tr>
          )}
          {(types.donnees ?? []).map((t) =>
            edition?.id === t.id ? (
              <tr key={t.id} className="bg-encre-50">
                <td className="py-1.5 pr-2">
                  <input className="champ py-1" value={edition.nature} onChange={(e) => setEdition({ ...edition, nature: e.target.value })} />
                </td>
                <td className="py-1.5 pr-2">
                  <input
                    className="champ tabulaire py-1"
                    value={edition.repertoire}
                    onChange={(e) => setEdition({ ...edition, repertoire: e.target.value })}
                  />
                </td>
                <td className="py-1.5 pr-2">{selectModele(edition.modele, (modele) => setEdition({ ...edition, modele }))}</td>
                <td className="py-1.5 pr-2" />
                <td className="py-1.5 pr-2" />
                <td className="whitespace-nowrap py-1.5 text-right">
                  <button type="button" className="text-xs underline" disabled={enCours} onClick={enregistrerEdition}>
                    Enregistrer
                  </button>
                  <button type="button" className="ml-2 text-xs text-encre-500 underline" onClick={() => setEdition(null)}>
                    Annuler
                  </button>
                </td>
              </tr>
            ) : (
              <tr key={t.id} className={t.actif ? '' : 'text-encre-400'}>
                <td className="py-1.5 pr-2 font-medium">{t.nature}</td>
                <td className="tabulaire py-1.5 pr-2">{t.repertoire}</td>
                <td className="py-1.5 pr-2">{libelleModele(t.modele)}</td>
                <td className="py-1.5 pr-2 text-center">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    aria-label={`Le type ${t.nature} porte les fiches de paie`}
                    checked={t.fiche_paie}
                    disabled={!peutModifier || enCours}
                    onChange={() => basculerFichePaie(t)}
                  />
                </td>
                <td className="py-1.5 pr-2">
                  <Badge ton={t.actif ? 'succes' : 'neutre'}>{t.actif ? 'actif' : 'inactif'}</Badge>
                  {t.nb_documents > 0 && (
                    <span className="ml-2 text-xs text-encre-500">
                      {t.nb_documents} document{t.nb_documents > 1 ? 's' : ''}
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap py-1.5 text-right">
                  {peutModifier && (
                    <>
                      <button
                        type="button"
                        className="text-xs underline"
                        onClick={() => setEdition({ id: t.id, nature: t.nature, repertoire: t.repertoire, modele: t.modele ?? '' })}
                      >
                        Modifier
                      </button>
                      <button type="button" className="ml-2 text-xs underline" disabled={enCours} onClick={() => basculer(t)}>
                        {t.actif ? 'Désactiver' : 'Activer'}
                      </button>
                      {t.nb_documents === 0 && (
                        <button type="button" className="ml-2 text-xs text-red-600 underline" onClick={() => supprimer(t)}>
                          Supprimer
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>

      {erreur && <p className="text-sm text-red-700">{erreur}</p>}

      <p className="text-xs text-encre-500">
        Variables : <span className="tabulaire">{'{annee}'}</span> année scolaire du document ·{' '}
        <span className="tabulaire">{'{ecole}'}</span> code de l’école · <span className="tabulaire">{'{mois}'}</span> AAAA-MM
        de production · <span className="tabulaire">{'{salarie}'}</span> NOM Prénom, pour un type rattaché à une personne.
        Les dossiers manquants sont créés, les existants réutilisés. Changer un répertoire ne vaut que pour les dépôts
        suivants : les liens des documents déjà archivés restent valides. Le réglage fait foi — un dossier renommé à la
        main dans le Drive doit l’être aussi ici.
      </p>
    </div>
  );
}
